import React, { useState, useEffect, useRef } from 'react';
import { QRCodeSVG } from 'qrcode.react';

const QRCodeGenerator = () => {
  const [value, setValue] = useState('');
  const [qrData, setQrData] = useState('');
  const [qrType, setQrType] = useState('text');
  const [size, setSize] = useState(256);
  const [fgColor, setFgColor] = useState('#000000');
  const [bgColor, setBgColor] = useState('#FFFFFF');
  const [level, setLevel] = useState('L');
  const [dotStyle, setDotStyle] = useState('square'); // square, dots, rounded
  const [cornerStyle, setCornerStyle] = useState('square'); // square, dot, extra-rounded
  const [gradientColor1, setGradientColor1] = useState('#000000');
  const [gradientColor2, setGradientColor2] = useState('#000000');
  const [useGradient, setUseGradient] = useState(false);
  const [image, setImage] = useState(null);
  const [imageSize, setImageSize] = useState(40); // Percentage of QR code size
  const [fileName, setFileName] = useState('qrcode');

  const [activeTab, setActiveTab] = useState('content');

  // States for different QR types
  const [wifiSsid, setWifiSsid] = useState('');
  const [wifiPassword, setWifiPassword] = useState('');
  const [wifiEncryption, setWifiEncryption] = useState('WPA');
  const [emailTo, setEmailTo] = useState('');
  const [emailSubject, setEmailSubject] = useState('');
  const [emailBody, setEmailBody] = useState('');
  const [phoneNum, setPhoneNum] = useState('');
  const [smsNum, setSmsNum] = useState('');
  const [smsBody, setSmsBody] = useState('');

  const qrCodeRef = useRef(null);

  // handleImageUpload function definition
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setImage(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    let data = '';
    switch (qrType) {
      case 'text':
        data = value;
        break;
      case 'url':
        data = value.startsWith('http') ? value : `http://${value}`;
        break;
      case 'wifi':
        // Ensure SSID and password are escaped for special characters
        data = `WIFI:S:${encodeURIComponent(wifiSsid)};T:${wifiEncryption};P:${encodeURIComponent(wifiPassword)};;`;
        break;
      case 'email':
        data = `mailto:${emailTo}?subject=${encodeURIComponent(emailSubject)}&body=${encodeURIComponent(emailBody)}`;
        break;
      case 'phone':
        data = `tel:${phoneNum}`;
        break;
      case 'sms':
        data = `smsto:${smsNum}:${encodeURIComponent(smsBody)}`;
        break;
      default:
        data = value;
    }
    setQrData(data);
  }, [value, qrType, wifiSsid, wifiPassword, wifiEncryption, emailTo, emailSubject, emailBody, phoneNum, smsNum, smsBody]);

  const downloadQrCode = (format) => {
    if (!qrCodeRef.current) return;

    const svgElement = qrCodeRef.current.querySelector('svg');
    if (!svgElement) return;

    const svgData = new XMLSerializer().serializeToString(svgElement);
    const filename = fileName || 'qrcode';

    if (format === 'png') {
      const canvas = document.createElement("canvas");
      const ctx = canvas.getContext("2d");
      const img = new Image();

      img.onload = () => {
        canvas.width = svgElement.width.baseVal.value;
        canvas.height = svgElement.height.baseVal.value;
        ctx.drawImage(img, 0, 0);
        const pngFile = canvas.toDataURL("image/png");
        const downloadLink = document.createElement("a");
        downloadLink.href = pngFile;
        downloadLink.download = `${filename}.png`;
        document.body.appendChild(downloadLink);
        downloadLink.click();
        document.body.removeChild(downloadLink);
      };
      img.src = 'data:image/svg+xml;base64,' + btoa(svgData);
    } else if (format === 'svg') {
      const downloadLink = document.createElement("a");
      downloadLink.href = 'data:image/svg+xml;base64,' + btoa(svgData);
      downloadLink.download = `${filename}.svg`;
      document.body.appendChild(downloadLink);
      downloadLink.click();
      document.body.removeChild(downloadLink);
    }
  };

  const renderInputFields = () => {
    switch (qrType) {
      case 'text':
        return (
          <div className="mb-3">
            <label htmlFor="textInput" className="form-label">Text</label>
            <input type="text" className="form-control" id="textInput" value={value} onChange={(e) => setValue(e.target.value)} placeholder="Enter your text" />
          </div>
        );
      case 'url':
        return (
          <div className="mb-3">
            <label htmlFor="urlInput" className="form-label">URL</label>
            <input type="url" className="form-control" id="urlInput" value={value} onChange={(e) => setValue(e.target.value)} placeholder="e.g., https://www.example.com" />
          </div>
        );
      case 'wifi':
        return (
          <>
            <div className="mb-3">
              <label htmlFor="wifiSsid" className="form-label">SSID (Network Name)</label>
              <input type="text" className="form-control" id="wifiSsid" value={wifiSsid} onChange={(e) => setWifiSsid(e.target.value)} placeholder="MyHomeNetwork" />
            </div>
            <div className="mb-3">
              <label htmlFor="wifiPassword" className="form-label">Password</label>
              <input type="password" className="form-control" id="wifiPassword" value={wifiPassword} onChange={(e) => setWifiPassword(e.target.value)} placeholder="Password" />
            </div>
            <div className="mb-3">
              <label htmlFor="wifiEncryption" className="form-label">Encryption</label>
              <select className="form-select" id="wifiEncryption" value={wifiEncryption} onChange={(e) => setWifiEncryption(e.target.value)}>
                <option value="WPA">WPA/WPA2</option>
                <option value="WEP">WEP</option>
                <option value="nopass">No Encryption</option>
              </select>
            </div>
          </>
        );
      case 'email':
        return (
          <>
            <div className="mb-3">
              <label htmlFor="emailTo" className="form-label">To</label>
              <input type="email" className="form-control" id="emailTo" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="recipient@example.com" />
            </div>
            <div className="mb-3">
              <label htmlFor="emailSubject" className="form-label">Subject</label>
              <input type="text" className="form-control" id="emailSubject" value={emailSubject} onChange={(e) => setEmailSubject(e.target.value)} placeholder="Subject of email" />
            </div>
            <div className="mb-3">
              <label htmlFor="emailBody" className="form-label">Body</label>
              <textarea className="form-control" id="emailBody" rows="3" value={emailBody} onChange={(e) => setEmailBody(e.target.value)} placeholder="Body of email"></textarea>
            </div>
          </>
        );
      case 'phone':
        return (
          <div className="mb-3">
            <label htmlFor="phoneNum" className="form-label">Phone Number</label>
            <input type="tel" className="form-control" id="phoneNum" value={phoneNum} onChange={(e) => setPhoneNum(e.target.value)} placeholder="+15551234567" />
          </div>
        );
      case 'sms':
        return (
          <>
            <div className="mb-3">
              <label htmlFor="smsNum" className="form-label">SMS Number</label>
              <input type="tel" className="form-control" id="smsNum" value={smsNum} onChange={(e) => setSmsNum(e.target.value)} placeholder="+15551234567" />
            </div>
            <div className="mb-3">
              <label htmlFor="smsBody" className="form-label">SMS Body</label>
              <textarea className="form-control" id="smsBody" rows="3" value={smsBody} onChange={(e) => setSmsBody(e.target.value)} placeholder="Your message"></textarea>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  return (
    <div className="container-fluid qr-generator-container">
      <div className="row justify-content-center align-items-center min-vh-100">
        <div className="col-lg-8 col-md-10 col-sm-12">
          <div className="card shadow-lg p-4">
            <div className="text-center mb-4">
              
              <h1 className="text-center text-primary">TagioQR</h1>
              <p className="lead text-muted">Your modern and easy-to-use QR Code Generator.</p>
            </div>

            <ul className="nav nav-tabs mb-3">
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'content' ? 'active' : ''}`} onClick={() => setActiveTab('content')}>Content</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'design' ? 'active' : ''}`} onClick={() => setActiveTab('design')}>Design</button>
              </li>
              <li className="nav-item">
                <button className={`nav-link ${activeTab === 'download' ? 'active' : ''}`} onClick={() => setActiveTab('download')}>Download</button>
              </li>
            </ul>

            <div className="tab-content">
              {activeTab === 'content' && (
                <div className="tab-pane fade show active">
                  <div className="mb-3">
                    <label htmlFor="qrTypeSelect" className="form-label">Select QR Code Type</label>
                    <select className="form-select" id="qrTypeSelect" value={qrType} onChange={(e) => {
                      setQrType(e.target.value);
                      setValue('');
                      setWifiSsid(''); setWifiPassword(''); setEmailTo(''); setEmailSubject(''); setEmailBody(''); setPhoneNum(''); setSmsNum(''); setSmsBody('');
                    }}>
                      <option value="text">Text</option>
                      <option value="url">URL</option>
                      <option value="wifi">Wi-Fi</option>
                      <option value="email">Email</option>
                      <option value="phone">Phone Number</option>
                      <option value="sms">SMS</option>
                    </select>
                  </div>
                  {renderInputFields()}
                </div>
              )}

              {activeTab === 'design' && (
                <div className="tab-pane fade show active">
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="sizeInput" className="form-label">Size (px)</label>
                      <input type="number" className="form-control" id="sizeInput" value={size} onChange={(e) => setSize(Number(e.target.value))} min="64" max="1024" step="64" />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="levelSelect" className="form-label">Error Correction</label>
                      <select className="form-select" id="levelSelect" value={level} onChange={(e) => setLevel(e.target.value)}>
                        <option value="L">Low (7%)</option>
                        <option value="M">Medium (15%)</option>
                        <option value="Q">Quartile (25%)</option>
                        <option value="H">High (30%)</option>
                      </select>
                    </div>
                  </div>
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="fgColorInput" className="form-label">Foreground Color</label>
                      <input type="color" className="form-control form-control-color" id="fgColorInput" value={fgColor} onChange={(e) => setFgColor(e.target.value)} />
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="bgColorInput" className="form-label">Background Color</label>
                      <input type="color" className="form-control form-control-color" id="bgColorInput" value={bgColor} onChange={(e) => setBgColor(e.target.value)} />
                    </div>
                  </div>
                  <div className="mb-3 form-check">
                    <input type="checkbox" className="form-check-input" id="useGradientCheck" checked={useGradient} onChange={(e) => setUseGradient(e.target.checked)} />
                    <label className="form-check-label" htmlFor="useGradientCheck">Use Gradient for Foreground</label>
                  </div>
                  {useGradient && (
                    <div className="row mb-3">
                      <div className="col-md-6">
                        <label htmlFor="gradientColor1Input" className="form-label">Gradient Color 1</label>
                        <input type="color" className="form-control form-control-color" id="gradientColor1Input" value={gradientColor1} onChange={(e) => setGradientColor1(e.target.value)} />
                      </div>
                      <div className="col-md-6">
                        <label htmlFor="gradientColor2Input" className="form-label">Gradient Color 2</label>
                        <input type="color" className="form-control form-control-color" id="gradientColor2Input" value={gradientColor2} onChange={(e) => setGradientColor2(e.target.value)} />
                      </div>
                    </div>
                  )}
                  <div className="row mb-3">
                    <div className="col-md-6">
                      <label htmlFor="dotStyleSelect" className="form-label">Dot Style</label>
                      <select className="form-select" id="dotStyleSelect" value={dotStyle} onChange={(e) => setDotStyle(e.target.value)}>
                        <option value="square">Square</option>
                        <option value="dots">Dots</option>
                        <option value="rounded">Rounded</option>
                      </select>
                    </div>
                    <div className="col-md-6">
                      <label htmlFor="cornerStyleSelect" className="form-label">Corner Style</label>
                      <select className="form-select" id="cornerStyleSelect" value={cornerStyle} onChange={(e) => setCornerStyle(e.target.value)}>
                        <option value="square">Square</option>
                        <option value="dot">Dot</option>
                        <option value="extra-rounded">Extra Rounded</option>
                      </select>
                    </div>
                  </div>
                  <div className="mb-3">
                    <label htmlFor="imageUpload" className="form-label">Upload Logo/Image (center)</label>
                    <input type="file" className="form-control" id="imageUpload" accept="image/*" onChange={handleImageUpload} />
                  </div>
                  {image && (
                    <div className="mb-3">
                      <label htmlFor="imageSizeInput" className="form-label">Logo Size (% of QR)</label>
                      <input type="range" className="form-range" id="imageSizeInput" min="10" max="50" value={imageSize} onChange={(e) => setImageSize(Number(e.target.value))} />
                      <small className="form-text text-muted">Adjust logo size. Too large may affect scannability.</small>
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'download' && (
                <div className="tab-pane fade show active">
                  <div className="mb-3">
                    <label htmlFor="fileNameInput" className="form-label">File Name</label>
                    <input type="text" className="form-control" id="fileNameInput" value={fileName} onChange={(e) => setFileName(e.target.value)} placeholder="qrcode" />
                  </div>
                  <p className="text-muted">Choose your preferred download format:</p>
                  <div className="d-grid gap-2 d-sm-flex justify-content-sm-center">
                    <button className="btn btn-success btn-lg" onClick={() => downloadQrCode('png')}>
                      Download PNG
                    </button>
                    <button className="btn btn-info btn-lg" onClick={() => downloadQrCode('svg')}>
                      Download SVG
                    </button>
                  </div>
                </div>
              )}
            </div>

            {qrData && (
              <div className="text-center mt-4">
                <div className="qr-code-display p-3 border rounded bg-white d-inline-block" ref={qrCodeRef}>
                  <QRCodeSVG
                    value={qrData}
                    size={size}
                    level={level}
                    fgColor={useGradient ? `url(#gradient)` : fgColor}
                    bgColor={bgColor}
                    imageSettings={image ? { src: image, x: undefined, y: undefined, height: size * (imageSize / 100), width: size * (imageSize / 100), excavate: true } : undefined}
                    options={{
                      dotsOptions: { type: dotStyle, color: fgColor },
                      cornersSquareOptions: { type: cornerStyle, color: fgColor },
                      cornersDotOptions: { type: cornerStyle, color: fgColor },
                    }}
                  />
                  {useGradient && (
                    <svg style={{ height: 0, width: 0, position: 'absolute' }}>
                      <defs>
                        <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stopColor={gradientColor1} />
                          <stop offset="100%" stopColor={gradientColor2} />
                        </linearGradient>
                      </defs>
                    </svg>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default QRCodeGenerator;